import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from '../components/Login';
import Dashboard from '../components/Dashboard';
import Devices from '../pages/Devices';
import Groups from '../pages/Groups';
import Hardware from '../pages/Hardware';
import Profiles from '../pages/Profiles';
import Roles from '../pages/Roles';
import Settings from '../pages/Settings';
import EventsAlarms from '../pages/EventsAlarms';
import Topology from '../pages/Topology';
import Utilities from '../pages/Utilities';

// How long a login is remembered across page reloads / closed tabs.
// Applies to password, Google, and Outlook logins alike, since all three
// call the same onLogin() callback below.
const SESSION_DURATION_MS = 10 * 60 * 1000; // 10 minutes
const SESSION_KEY = 'racs_login_timestamp';

const getStoredLoginTime = (): number | null => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isNaN(value) ? null : value;
};

const isSessionStillValid = (): boolean => {
    const loginTime = getStoredLoginTime();
    if (loginTime === null) return false;
    return Date.now() - loginTime < SESSION_DURATION_MS;
};

const App: React.FC = () => {
    // On first render (including after reopening a closed tab), restore the
    // session if a valid timestamp is still within the 10-minute window.
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => isSessionStillValid());

    const handleLogin = useCallback(() => {
        localStorage.setItem(SESSION_KEY, String(Date.now()));
        setIsAuthenticated(true);
    }, []);

    const handleLogout = useCallback(() => {
        localStorage.removeItem(SESSION_KEY);
        setIsAuthenticated(false);
    }, []);

    // Automatically log out once the 10-minute window elapses, even if the
    // tab is left open the whole time (not just on reload).
    useEffect(() => {
        if (!isAuthenticated) return;

        const loginTime = getStoredLoginTime();
        if (loginTime === null) {
            handleLogout();
            return;
        }

        const remaining = SESSION_DURATION_MS - (Date.now() - loginTime);
        if (remaining <= 0) {
            handleLogout();
            return;
        }

        const timer = setTimeout(handleLogout, remaining);
        return () => clearTimeout(timer);
    }, [isAuthenticated, handleLogout]);

    return (
        <Router>
            <Routes>
                <Route
                    path="/"
                    element={
                        isAuthenticated ? (
                            <Navigate to="/dashboard" />
                        ) : (
                            <Login onLogin={handleLogin} />
                        )
                    }
                />

                <Route element={isAuthenticated ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/" />}>                    <Route path="dashboard" element={<Devices />} />
                    <Route path="devices" element={<Devices />} />
                    <Route path="groups" element={<Groups />} />
                    <Route path="hardware" element={<Hardware />} />
                    <Route path="profiles" element={<Profiles />} />
                    <Route path="roles" element={<Roles />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="events" element={<EventsAlarms />} />
                    <Route path="topology" element={<Topology />} />
                    <Route path="utilities" element={<Utilities />} />
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                </Route>
            </Routes>
        </Router>
    );
};

export default App;